import { Component, inject, ChangeDetectorRef, OnInit, effect, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AppStateService } from '../../services/app-state.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Web3Service } from '../../services/web3.service';
import { ActivityService } from '../../services/activity.service';
import { ERC20_ABI } from '../../config/contracts.config';
import { ethers } from 'ethers';
import { formatSubscript } from '../../utils/number.util';

export interface StakingPoolCard {
  id: string;
  name: string;
  tokenName: string;
  badge: 'Core' | 'Exclusive';
  apy: number;
  tvl: string;
  rewardToken: string;
  lockTiers?: any[];
  color: string;
  poolId?: number;
  stakingAddress?: string;
  stakingTokenAddress?: string;
  rawTotalStaked?: number;
  logoUrl?: string;
  rewardTokenLogo?: string;
  rewardTokenAddress?: string;
}

export interface UserStake {
  _uiId?: string;
  depositIndex: number;
  amount: number;
  effectiveAmount: number;
  multiplier?: number;
  lockExpiry: number;
  pool: StakingPoolCard;
  pendingReward: string;
}

import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-stake-page',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './stake-page.component.html',
})
export class StakePageComponent {
  public state = inject(AppStateService);
  private authService = inject(AuthService);
  private http = inject(HttpClient);
  private web3Service = inject(Web3Service);
  private activityService = inject(ActivityService);
  private cdr = inject(ChangeDetectorRef);

  searchQuery = '';
  selectedNetwork = 'All Networks';
  sortOption = 'APY (High to Low)';

  pools: StakingPoolCard[] = [];
  userStakes: UserStake[] = [];
  isLoading = true;
  isLoadingLiveStats = false;

  stakeModalOpen: boolean = false;
  selectedPool: StakingPoolCard | null = null;
  stakeAmount: string = '';
  selectedTier: number = 0; // 0 = flexible, 1 = 30 days, 2 = 90 days
  stakingStatus: string = '';
  formatSubscript = formatSubscript;

  get selectedTokenBalance(): number {
    if (!this.selectedPool) return 0;
    const tokens = this.state.tokens();
    const token = tokens.find(t => t.symbol === this.selectedPool?.tokenName);
    if (!token || !token.address) return 0;
    const balanceStr = this.state.tokenBalances()[token.address.toLowerCase()] || '0';
    return parseFloat(balanceStr);
  }

  Math = Math;

  formatNumber(value: number): string {
    if (value === undefined || value === null) return '0.00';
    if (value >= 1000000000000) return (value / 1000000000000).toFixed(2) + 'T';
    if (value >= 1000000000) return (value / 1000000000).toFixed(2) + 'B';
    if (value >= 1000000) return (value / 1000000).toFixed(2) + 'M';
    if (value >= 1000) return (value / 1000).toFixed(2) + 'K';
    return value.toFixed(2);
  }

  isWithdrawing: Record<string, boolean> = {};
  isClaiming: Record<string, boolean> = {};

  poolsPage = 1;
  poolsLimit = 5;
  poolsTotal = 0;

  stakesPage = 1;
  stakesLimit = 5;
  stakesTotal = 0;
  isStakesLoading = false;

  _filteredPoolsCache: StakingPoolCard[] = [];

  globalStats: any = {
    totalValueStaked: 0,
    avgApr: 0,
    activeStakers: 0,
    totalPools: 0,
  };

  constructor() {
    effect(() => {
      const networkId = this.state.selectedNetworkId();
      untracked(() => {
        this.fetchStats();
        this.fetchPools();
      });
      if (this.state.isWalletConnected() && this.pools.length > 0) {
        untracked(() => {
          this.fetchUserStakes();
        });
      } else if (!this.state.isWalletConnected()) {
        this.userStakes = [];
        this.cdr.markForCheck();
      }
    });
  }

  ngOnInit() { }

  fetchStats() {
    const chainId = this.web3Service.chainId() || 1;
    this.http.get<any>(`${environment.apiUrl}/user/staking/stats?chainId=${chainId}`).subscribe({
      next: (res) => {
        if (res && res.status && res.data) {
          this.globalStats = res.data;
          this.cdr.markForCheck();
        }
      },
      error: (err) => console.error('Failed to fetch stats', err),
    });
  }

  fetchPools() {
    this.isLoading = true;
    const chainId = this.web3Service.chainId() || 1;

    this.http
      .get<any>(
        `${environment.apiUrl}/user/staking/list?page=${this.poolsPage}&limit=${this.poolsLimit}&search=${this.searchQuery}&chainId=${chainId}`)
      .subscribe({
        next: (res) => {
          if (res && res.status && res.data && res.data.data) {
            const globalTokens = this.state.tokens();
            this.pools = res.data.data.map((pool: any) => {
              const tok = globalTokens.find(t => t.symbol === pool.stakingTokenSymbol);
              const maxApyBps = pool.lockTiers && pool.lockTiers.length > 0
                ? Math.max(...pool.lockTiers.map((t: any) => t.apyBps || 0))
                : 0;

              let finalLogoUrl = tok?.logoUrl || pool.logoUrl || '';
              if (!finalLogoUrl && pool.stakingTokenSymbol) {
                const sym = pool.stakingTokenSymbol.toUpperCase();
                if (sym === 'ETH' || sym === 'WETH') finalLogoUrl = 'https://cryptologos.cc/logos/ethereum-eth-logo.png';
                else if (sym === 'BNB' || sym === 'WBNB') finalLogoUrl = 'https://cryptologos.cc/logos/bnb-bnb-logo.png';
                else if (sym === 'POL' || sym === 'MATIC' || sym === 'WMATIC') finalLogoUrl = 'https://cryptologos.cc/logos/polygon-matic-logo.png';
                else if (sym === 'USDC') finalLogoUrl = 'https://cryptologos.cc/logos/usd-coin-usdc-logo.png';
                else if (sym === 'USDT') finalLogoUrl = 'https://cryptologos.cc/logos/tether-usdt-logo.png';
              }

              let rewardTokenLogo = '';
              if (pool.rewardTokenSymbol) {
                const rSym = pool.rewardTokenSymbol.toUpperCase();
                if (rSym === 'ETH' || rSym === 'WETH') rewardTokenLogo = 'https://cryptologos.cc/logos/ethereum-eth-logo.png';
                else if (rSym === 'BNB' || rSym === 'WBNB') rewardTokenLogo = 'https://cryptologos.cc/logos/bnb-bnb-logo.png';
                else if (rSym === 'POL' || rSym === 'MATIC' || rSym === 'WMATIC') rewardTokenLogo = 'https://cryptologos.cc/logos/polygon-matic-logo.png';
                else if (rSym === 'USDC') rewardTokenLogo = 'https://cryptologos.cc/logos/usd-coin-usdc-logo.png';
                else if (rSym === 'USDT') rewardTokenLogo = 'https://cryptologos.cc/logos/tether-usdt-logo.png';
              }

              const displaySymbol = (pool.stakingTokenSymbol === 'DXOR' || pool.stakingTokenSymbol === 'DEXOR') ? 'DEX' : (pool.stakingTokenSymbol || 'Unknown');
              const displayRewardSymbol = (pool.rewardTokenSymbol === 'DXOR' || pool.rewardTokenSymbol === 'DEXOR') ? 'DEX' : (pool.rewardTokenSymbol || 'Reward');

              return {
                id: pool.stakingAddress || '',
                stakingAddress: pool.stakingAddress || '',
                name: `${displaySymbol} Staking`,
                tokenName: displaySymbol,
                stakingTokenAddress: pool.stakingTokenAddress || '',
                badge: displaySymbol === 'DEX' ? 'Exclusive' : 'Core',
                apy: maxApyBps / 100, // Converts BPS to percentage (e.g. 2200 -> 22%)
                rawTotalStaked: Number(pool.totalStaked || 0),
                tvl: `${Number(pool.totalStaked || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${displaySymbol}`,
                rewardToken: displayRewardSymbol,
                rewardTokenAddress: pool.rewardTokenAddress || '',
                rewardTokenLogo: rewardTokenLogo,
                lockTiers: pool.lockTiers || [], // Pass lock tiers for UI
                color: displaySymbol === 'DEX' ? '#F5B73B' : '#627eea',
                logoUrl: finalLogoUrl,
                poolId: pool.poolId,
              };
            });
            this.poolsTotal = res.data.count;
            this.updateFilteredPools();
            this.fetchLivePoolStats(); // Update live values on the frontend
            if (this.state.isWalletConnected()) {
              this.fetchUserStakes();
            }
          }
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Failed to load staking pools', err);
          this.isLoading = false;
          this.cdr.detectChanges();
        },
      });
  }

  async fetchLivePoolStats() {
    this.isLoadingLiveStats = true;
    this.cdr.detectChanges();
    try {
      const provider = await this.web3Service.getEthersProvider();
      if (!provider) return;

      let totalGlobalTvl = 0;
      let totalApr = 0;
      let validAprPools = 0;

      const ERC20_ABI = [
        'function balanceOf(address) view returns (uint256)',
        'function decimals() view returns (uint8)'
      ];
      const MASTER_STAKING_ABI = ['function getLockTiers(uint256) view returns (tuple(uint256 duration, uint32 apyBps)[])'];

      for (let pool of this.pools) {
        if (!pool.stakingTokenAddress || !pool.stakingAddress) continue;
        try {
          let formattedBal = 0;
          if (pool.stakingTokenAddress.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee') {
            const bal = await provider.getBalance(pool.stakingAddress);
            formattedBal = Number(ethers.formatEther(bal));
          } else {
            const contract = new ethers.Contract(pool.stakingTokenAddress, ERC20_ABI, provider);
            const bal = await contract['balanceOf'](pool.stakingAddress);
            let dec = 18;
            try {
              dec = Number(await contract['decimals']());
            } catch (e) {
              console.warn('Could not fetch decimals for', pool.stakingTokenAddress);
            }
            formattedBal = Number(ethers.formatUnits(bal, dec));
          }

          const globalTokens = this.state.tokens();
          const tokenPrice = globalTokens.find((t: any) => 
            t.address?.toLowerCase() === pool.stakingTokenAddress?.toLowerCase() ||
            (pool.stakingTokenAddress?.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee' && t.address === 'native')
          )?.rate || 0;
          
          const usdValue = formattedBal * tokenPrice;

          pool.rawTotalStaked = formattedBal;
          pool.tvl = `${formattedBal.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${pool.tokenName}`;
          totalGlobalTvl += usdValue;
        } catch (e) {
          console.error('Error fetching balance for pool', pool.id);
        }

        try {
          const maxApyBps = pool.lockTiers && pool.lockTiers.length > 0
            ? Math.max(...pool.lockTiers.map((t: any) => t.apyBps || 0))
            : 0;
          pool.apy = maxApyBps / 100;

          if (pool.apy > 0) {
            totalApr += pool.apy;
            validAprPools++;
          }
        } catch (e) {
          console.error('Error calculating APY for pool', pool.id);
        }
      }

      if (this.pools.length > 0) {
        this.globalStats = {
          ...this.globalStats,
          totalValueStaked: totalGlobalTvl,
          avgApr: validAprPools > 0 ? (totalApr / validAprPools) : 0,
          totalPools: this.pools.length
        };
      } else {
        this.globalStats = {
          ...this.globalStats,
          totalValueStaked: 0,
          avgApr: 0,
          totalPools: 0
        };
      }

      this.updateFilteredPools();
    } finally {
      this.isLoadingLiveStats = false;
      this.cdr.detectChanges();
    }
  }

  nextPoolsPage() {
    if (this.poolsPage * this.poolsLimit < this.poolsTotal) {
      this.poolsPage++;
      this.fetchPools();
    }
  }

  prevPoolsPage() {
    if (this.poolsPage > 1) {
      this.poolsPage--;
      this.fetchPools();
    }
  }

  updateFilteredPools() {
    let result = [...this.pools];

    if (this.searchQuery) {
      const q = this.searchQuery.toLowerCase();
      result = result.filter(
        (p) => p.name.toLowerCase().includes(q) || p.tokenName.toLowerCase().includes(q),
      );
    }

    if (this.sortOption === 'APY (High to Low)') {
      result.sort((a, b) => b.apy - a.apy);
    } else if (this.sortOption === 'APY (Low to High)') {
      result.sort((a, b) => a.apy - b.apy);
    }

    this._filteredPoolsCache = result.slice(0, 6);
  }

  get filteredPools() {
    return this._filteredPoolsCache;
  }

  getFallbackLogoUrl(sym: string): string {
    if (!sym) return '';
    const s = sym.toUpperCase();
    if (s === 'ETH' || s === 'WETH') return 'https://cryptologos.cc/logos/ethereum-eth-logo.png';
    if (s === 'BNB' || s === 'WBNB') return 'https://cryptologos.cc/logos/bnb-bnb-logo.png';
    if (s === 'POL' || s === 'MATIC' || s === 'WMATIC') return 'https://cryptologos.cc/logos/polygon-matic-logo.png';
    if (s === 'USDC') return 'https://cryptologos.cc/logos/usd-coin-usdc-logo.png';
    if (s === 'USDT') return 'https://cryptologos.cc/logos/tether-usdt-logo.png';
    return '';
  }

  fetchUserStakes() {
    const userAddress = this.web3Service.address();
    if (!userAddress) return;

    this.isStakesLoading = true;
    this.cdr.markForCheck();

    const authHeaders = { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` };
    const chainId = this.web3Service.chainId() || 1;

    const doFetch = () => {
      this.http
        .get<any>(
          `${environment.apiUrl}/user/staking/user-deposits/${userAddress}?page=${this.stakesPage}&limit=${this.stakesLimit}&chainId=${chainId}`,
        )
        .subscribe({
          next: (res) => {
            if (res && res.status && res.data && res.data.data) {
              const globalTokens = this.state.tokens();
              this.userStakes = res.data.data.map((d: any) => {
                const tok = globalTokens.find(t => t.symbol === d.pool?.tokenName);

                const activePoolMatch = this.pools.find(p => p.stakingTokenAddress?.toLowerCase() === d.pool?.stakingTokenAddress?.toLowerCase());
                const correctPoolId = activePoolMatch && activePoolMatch.poolId !== undefined ? activePoolMatch.poolId : d.pool?.poolId;

                console.log('DEBUG STAKE:', {
                  dPoolId: d.pool?.poolId,
                  dStakingTokenAddress: d.pool?.stakingTokenAddress,
                  activePoolMatchPoolId: activePoolMatch?.poolId,
                  correctPoolId: correctPoolId
                });

                const fullPool = this.pools.find(p => p.stakingAddress === d.pool?.stakingAddress && p.poolId === correctPoolId);
                const sym = d.pool?.tokenName || d.pool?.stakingTokenSymbol;
                let rSym = d.pool?.rewardToken || d.pool?.rewardTokenSymbol;

                // FORCE native token symbol if rewardTokenAddress is 0xeeee...
                if (fullPool?.rewardTokenAddress?.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee') {
                  rSym = chainId === 137 ? 'POL' : (chainId === 56 ? 'BNB' : 'ETH');
                }

                const rTok = globalTokens.find(t => t.symbol === rSym);

                return {
                  _uiId: d._id || Math.random().toString(36).substr(2, 9),
                  depositIndex: d.depositIndex,
                  amount: d.amount,
                  effectiveAmount: d.effectiveAmount,
                  multiplier: d.multiplier,
                  apyBps: d.apyBps || 0,
                  tierIndex: d.tierIndex || 0,
                  lockExpiry: d.lockExpiry || 0,
                  pool: {
                    ...d.pool,
                    poolId: correctPoolId, // Override poolId
                    name: d.pool?.name || (sym ? `${sym} Staking` : 'Unknown Staking'),
                    tokenName: sym || 'Unknown',
                    stakingAddress: d.pool?.stakingAddress,
                    color: d.pool?.color || this.state.getRandomColor(sym || 'U'),
                    logoUrl: d.pool?.logoUrl || tok?.logoUrl || this.getFallbackLogoUrl(sym),
                    rewardToken: rSym, // Override rewardToken
                    rewardTokenLogo: d.pool?.rewardTokenLogo || rTok?.logoUrl || this.getFallbackLogoUrl(rSym),
                    lockTiers: fullPool?.lockTiers || d.pool?.lockTiers || []
                  },
                  pendingReward: Number(d.pendingReward || 0).toString(),
                };
              });
              this.stakesTotal = res.data.count;

              this.fetchLivePendingRewards();
            }
            this.isStakesLoading = false;
            this.cdr.detectChanges();
          },
          error: (err) => {
            console.error('Failed to fetch user stakes', err);
            this.isStakesLoading = false;
            this.cdr.detectChanges();
          },
        });
    };

    doFetch();
  }

  async fetchLivePendingRewards() {
    try {
      const provider = await this.web3Service.getEthersProvider();
      if (!provider) return;
      const userAddress = this.web3Service.address();
      const globalTokens = this.state.tokens();

      const promises = this.userStakes.map(async (stake, idx) => {
        if (!stake.pool?.stakingAddress) return;
        const MASTER_STAKING_ABI = [
          'function getUserDeposits(address user, uint256 poolId) view returns (tuple(uint256 amount, uint256 lockExpiry, uint256 tierIndex)[])',
          'function pendingReward(address user, uint256 poolId, uint256 depositIndex) view returns (uint256)'
        ];

        try {
          const rTok = globalTokens.find(t => t.symbol === stake.pool?.rewardToken);
          const decimals = rTok?.decimals || 18;

          console.log(`[DEBUG] Calling pendingReward for index ${idx}:`, {
            poolId: stake.pool.poolId,
            depositIndex: stake.depositIndex,
            stakingAddress: stake.pool.stakingAddress
          });

          const contract = new ethers.Contract(stake.pool.stakingAddress, MASTER_STAKING_ABI, provider);
          const pending = await contract['pendingReward'](userAddress, stake.pool.poolId, stake.depositIndex);
          this.userStakes[idx].pendingReward = ethers.formatUnits(pending, decimals);
          this.cdr.markForCheck();
        } catch (e) {
          console.error(`Failed to live-fetch pending reward for idx ${idx}, poolId: ${stake.pool.poolId}, depositIndex: ${stake.depositIndex}`, e);
        }
      });

      await Promise.all(promises);
    } catch (e) {
      console.error('Error in fetchLivePendingRewards', e);
    }
  }

  nextStakesPage() {
    if (this.stakesPage * this.stakesLimit < this.stakesTotal) {
      this.stakesPage++;
      this.fetchUserStakes();
    }
  }

  prevStakesPage() {
    if (this.stakesPage > 1) {
      this.stakesPage--;
      this.fetchUserStakes();
    }
  }

  async openStakeModal(pool: StakingPoolCard) {
    if (!this.state.isWalletConnected()) {
      this.state.openConnectModal();
      return;
    }

    if (!this.authService.isAuthenticated()) {
      try {
        await this.authService.login(this.web3Service.address() || '');
      } catch (err) {
        this.state.triggerNotification('You must sign the message to proceed.', false);
        return;
      }
    }

    this.selectedPool = pool;
    this.stakeAmount = '';
    this.stakeModalOpen = true;
    this.stakingStatus = '';
  }

  closeStakeModal() {
    if (this.stakingStatus && this.stakingStatus !== 'SUCCESS!') {
      this.state.triggerNotification('Please wait for the transaction to complete.', false);
      return;
    }
    this.stakeModalOpen = false;
    this.selectedPool = null;
  }

  async deposit() {
    if (
      !this.selectedPool ||
      !this.selectedPool.stakingAddress ||
      !this.selectedPool.stakingTokenAddress ||
      !this.stakeAmount
    )
      return;
    try {
      this.stakingStatus = 'PREPARING...';
      this.cdr.markForCheck();

      let decimals = 18;
      try {
        if (this.selectedPool.stakingTokenAddress.toLowerCase() !== '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee') {
          const signer = await this.web3Service.getSigner();
          const tokenContract = new ethers.Contract(this.selectedPool.stakingTokenAddress, ERC20_ABI, signer);
          decimals = Number(await tokenContract['decimals']());


        }
      } catch (e) {
        console.error('Failed to fetch decimals for staking token, using 18', e);
      }

      this.stakingStatus = 'APPROVING...';
      this.cdr.markForCheck();
      const amountWei = ethers.parseUnits(this.stakeAmount.toString(), decimals);

      const tx = await this.web3Service.depositStaking(
        this.selectedPool.stakingTokenAddress,
        this.selectedPool.stakingAddress,
        this.selectedPool.poolId !== undefined ? this.selectedPool.poolId : 0,
        amountWei,
        this.selectedTier,
        (status) => {
          this.stakingStatus = status;
          this.cdr.markForCheck();
        },
      );

      if (tx) {
        this.stakingStatus = 'SUCCESS!';
        this.cdr.markForCheck();

        try {
          const iface = new ethers.Interface([
            'event Deposited(address indexed user, uint256 indexed poolId, uint256 indexed depositIndex, uint256 amount, uint256 tierIndex, uint256 lockExpiry)'
          ]);
          
          let parsedDepositId: number | undefined = undefined;
          let parsedAmount = Number(this.stakeAmount); // Default to UI amount
          let parsedLockExpiry = 0;

          if (tx.logs) {
            for (const log of tx.logs) {
              try {
                const parsedLog = iface.parseLog({ topics: log.topics, data: log.data });
                if (parsedLog && parsedLog.name === 'Deposited') {
                  parsedDepositId = Number(parsedLog.args['depositIndex']); 
                  parsedAmount = Number(ethers.formatUnits(parsedLog.args['amount'], decimals)); 
                  parsedLockExpiry = Number(parsedLog.args['lockExpiry']); 
                }
              } catch (e) { }
            }
          }

          let multiplier = 1;
          let parsedEffectiveAmount = parsedAmount;
          let tierIndex = this.selectedTier || 0;
          let apyBps = this.selectedPool?.lockTiers ? this.selectedPool.lockTiers[tierIndex]?.apyBps || 0 : 0;

          const authHeaders = { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` };
          await import('rxjs').then((m) =>
            m.firstValueFrom(
              this.http.post(
                `${environment.apiUrl}/user/staking/user-deposits/sync?chainId=${this.web3Service.chainId() || 1}`,
                {
                  txHash: tx.hash,
                  chainId: this.web3Service.chainId() || 1,
                  type: 'DEPOSIT',
                  userAddress: this.web3Service.address(),
                  poolAddress: this.selectedPool?.stakingTokenAddress,
                  depositId: parsedDepositId,
                  amount: parsedAmount,
                  multiplier: multiplier,
                  effectiveAmount: parsedEffectiveAmount,
                  lockDuration: this.selectedPool?.lockTiers ? this.selectedPool.lockTiers[this.selectedTier]?.duration : 0,
                  lockExpiry: parsedLockExpiry,
                  tierIndex: tierIndex,
                  apyBps: apyBps,
                },
                { headers: authHeaders },
              ),
            ),
          );

          // Unified Activity Log
          await import('rxjs').then((m) =>
            m.firstValueFrom(
              this.activityService.recordActivity({
                txHash: tx.hash,
                chainId: this.web3Service.chainId() || 1,
                actionType: 'STAKE_NORMAL',
                status: 'Success',
                payload: {
                  amount: Number(this.stakeAmount),
                  lockDuration: this.selectedTier,
                  poolAddress: this.selectedPool?.stakingAddress,
                  tokenSymbol: this.selectedPool?.tokenName,
                },
                platformName: 'DEX Staking',
              }),
            ),
          );
        } catch (syncErr) {
          console.error('Failed to sync deposit with backend:', syncErr);
        }

        setTimeout(() => {
          this.closeStakeModal();
          this.fetchUserStakes();
          this.cdr.markForCheck();
        }, 2000);
      }
    } catch (e: any) {
      console.error(e);
      this.state.triggerNotification(
        this.state.handleError(e, 'Transaction failed'),
        false,
      );
    } finally {
      if (this.stakingStatus !== 'SUCCESS!') {
        this.stakingStatus = '';
        this.cdr.markForCheck();
      }
    }
  }

  async withdraw(stake: UserStake) {
    if (!stake.pool.stakingAddress) return;

    if (!this.authService.isAuthenticated()) {
      try {
        await this.authService.login(this.web3Service.address() || '');
      } catch (err) {
        this.state.triggerNotification('You must sign the message to withdraw.', false);
        return;
      }
    }

    const stakeKey = `${stake.pool.stakingAddress}-${stake.pool.stakingTokenAddress}-${stake.depositIndex}-${stake['_uiId'] || Math.random()}`;

    this.isWithdrawing = { ...this.isWithdrawing, [stakeKey]: true };
    this.cdr.markForCheck();

    try {
      const signer = await this.web3Service.getSigner();
      const contract = new ethers.Contract(
        stake.pool.stakingAddress,
        ['function withdraw(uint256,uint256)'],
        signer
      );

      const tx = await contract['withdraw'](stake.pool.poolId, stake.depositIndex);
      this.state.triggerNotification(`Withdrawal submitted: ${tx.hash.slice(0, 10)}...`);
      await tx.wait();
      this.state.triggerNotification('Withdrawn successfully!');

      const pendingReward = stake.pendingReward;
      this.userStakes = this.userStakes.filter(
        (s) =>
          !(
            s.pool.stakingAddress === stake.pool.stakingAddress &&
            s.depositIndex === stake.depositIndex
          ),
      );
      this.cdr.markForCheck();

      if (Number(pendingReward) > 0) {
        await import('rxjs').then((m) =>
          m.firstValueFrom(
            this.activityService.recordActivity({
              txHash: tx.hash,
              chainId: this.web3Service.chainId() || 1,
              actionType: 'CLAIM_STAKE_REWARD',
              status: 'Success',
              payload: {
                poolAddress: stake.pool.stakingAddress,
                tokenSymbol: stake.pool.tokenName,
                rewardAmount: ethers
                  .parseUnits(pendingReward.toString(), (stake.pool as any).tokenDecimals || 18)
                  .toString(),
                tokenAddress: (stake.pool as any).tokenAddress || stake.pool.stakingTokenAddress,
              },
              platformName: 'DEX Staking',
            }),
          ),
        ).catch(err => console.warn('Failed to record automatic claim on unstake', err));
      }

      // Unified Activity Log
      await import('rxjs').then((m) =>
        m.firstValueFrom(
          this.activityService.recordActivity({
            txHash: tx.hash,
            chainId: this.web3Service.chainId() || 1,
            actionType: 'UNSTAKE_NORMAL',
            status: 'Success',
            payload: {
              amount: stake.amount,
              poolAddress: stake.pool.stakingAddress,
              tokenSymbol: stake.pool.tokenName,
            },
            platformName: 'DEX Staking',
          }),
        ),
      );

      try {
        const authHeaders = { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` };

        // The user stake object already has the depositId and poolAddress
        // since it was fetched from the DB initially
        await import('rxjs').then((m) =>
          m.firstValueFrom(
            this.http.post(
              `${environment.apiUrl}/user/staking/user-deposits/sync?chainId=${this.web3Service.chainId() || 1}`,
              {
                txHash: tx.hash,
                chainId: this.web3Service.chainId() || 1,
                type: 'WITHDRAW',
                userAddress: this.web3Service.address(),
                poolAddress: stake.pool.stakingTokenAddress,
                depositId: stake.depositIndex
              },
              { headers: authHeaders },
            ),
          ),
        );
      } catch (syncErr) {
        console.error('Failed to sync withdrawal with backend:', syncErr);
      }

      setTimeout(() => this.fetchUserStakes(), 2000);
    } catch (e) {
      console.error(e);
      this.state.triggerNotification(this.state.handleError(e, 'Withdrawal failed. Check if lock has expired.'), false);
      this.fetchUserStakes();
    } finally {
      this.isWithdrawing = { ...this.isWithdrawing, [stakeKey]: false };
      this.cdr.markForCheck();
    }
  }

  async claimRewards(stake: UserStake) {
    if (!stake.pool.stakingAddress) return;

    if (Number(stake.pendingReward) <= 0) {
      this.state.triggerNotification('No rewards available to claim yet.', false);
      return;
    }

    if (!this.authService.isAuthenticated()) {
      try {
        await this.authService.login(this.web3Service.address() || '');
      } catch (err) {
        this.state.triggerNotification('You must sign the message to claim.', false);
        return;
      }
    }

    const stakeKey = `${stake.pool.stakingAddress}-${stake.pool.stakingTokenAddress}-${stake.depositIndex}-${stake['_uiId'] || Math.random()}`;
    this.isClaiming = { ...this.isClaiming, [stakeKey]: true };
    this.cdr.markForCheck();

    try {
      const signer = await this.web3Service.getSigner();
      const contract = new ethers.Contract(
        stake.pool.stakingAddress,
        ['function claim(uint256,uint256)'],
        signer
      );

      const tx = await contract['claim'](stake.pool.poolId, stake.depositIndex);
      this.state.triggerNotification(`Claim submitted: ${tx.hash.slice(0, 10)}...`);
      await tx.wait();
      this.state.triggerNotification('Rewards claimed successfully!');

      const pendingReward = stake.pendingReward;

      stake.pendingReward = '0.00';
      this.cdr.markForCheck();

      await import('rxjs').then((m) =>
        m.firstValueFrom(
          this.activityService.recordActivity({
            txHash: tx.hash,
            chainId: this.web3Service.chainId() || 1,
            actionType: 'CLAIM_STAKE_REWARD',
            status: 'Success',
            payload: {
              poolAddress: stake.pool.stakingAddress,
              tokenSymbol: stake.pool.tokenName,
              rewardAmount: ethers
                .parseUnits(pendingReward.toString(), (stake.pool as any).tokenDecimals || 18)
                .toString(),
              tokenAddress: (stake.pool as any).tokenAddress || stake.pool.stakingTokenAddress,
            },
            platformName: 'DEX Staking',
          }),
        ),
      );

      setTimeout(() => this.fetchUserStakes(), 2000);
    } catch (e: any) {
      console.error(e);
      let errorMsg = this.state.handleError(e, 'Claim rewards failed.');
      if (e.code === 'CALL_EXCEPTION') {
        errorMsg = 'Smart contract rejected the claim. Either rewards are zero, or pool is empty.';
      }
      this.state.triggerNotification(errorMsg, false);
    } finally {
      this.isClaiming = { ...this.isClaiming, [stakeKey]: false };
      this.cdr.markForCheck();
    }
  }

  getStakeApy(stake: any): number {
    if (stake.apyBps && stake.apyBps > 0) {
      return stake.apyBps / 100;
    }
    if (!stake?.pool?.lockTiers || stake.pool.lockTiers.length === 0) return 0;

    // Match the tier by tierIndex
    const tierIdx = stake.tierIndex || 0;
    const tier = stake.pool.lockTiers[tierIdx];

    if (tier) {
      return (tier.apyBps || 0) / 100;
    }

    return 0;
  }

  getTimeRemaining(lockExpiry: string | Date | number): string {
    if (!lockExpiry) return '';
    const expiry = new Date(lockExpiry).getTime();
    const now = new Date().getTime();
    if (expiry <= now) return '';
    
    const diff = expiry - now;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const mins = Math.floor((diff / 1000 / 60) % 60);
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  }
  
  isLocked(stake: any): boolean {
    if (!stake.lockExpiry) return false;
    return new Date(stake.lockExpiry).getTime() > new Date().getTime();
  }

  hasNoRewards(stake: any): boolean {
    if (!stake || !stake.pendingReward) return true;
    const num = Number(stake.pendingReward);
    return isNaN(num) || num <= 0;
  }
}
